import { connect } from 'react-redux';

import ReusableGridButton from '../../components/grid-header/reusable-grid-button';
import { ModalService } from 'ui-components/services/modal-service';

import { RestoreModal } from '../../components/modals/restore-modal';
import { RestoreResultsModal } from '../../components/modals/restore-results-modal';

import Global from '../../store/global-selectors';
import { launchRemoveHoldModal, launchHoldUntilModal } from '../../store/hold-until/hold-until.actions';
import { orderGridOpenMarkShippedModal, orderGridGetRates } from '../../store/orders/grid/orders.grid.header.actions';
import { BASE_MAS_PAGE } from '../../orders/mark-as-shipped/constants';
import { prepareToOpenNewOrderModal } from '../../store/orders/edit-order/edit.order.actions';
import { requestRestoreFulfillments, requestRestoreOrder, openMarkAsPaidModal } from '../../store/orders/orders.actions';
import { getActualFulfillmentPlanIds, getActualSalesOrderIds } from '../../store/orders/orders.helpers';
import { uniqueMarketPlaceIdsSelector } from '../orders-drawer/sections/use-order-drawer-section';
import { PusherService } from '../../services/pusher-service';
import { messages } from './messages';
import { MarketplaceIds } from '../../enums/marketplace-ids';
import { WorkflowSteps, trackWorkflow } from '../../tracking/use-workflow-tracking';
import { EventSourceStrings, Workflows } from '../../tracking/tracking-constants';

const mapStateToProps = (state) => {
  const selectedOrderIds = Global.selectSelectedOrdersIds(state);
  const actualSelectedSalesOrderIds = getActualSalesOrderIds(state, selectedOrderIds);
  const selectedFulfillmentPlanIds = getActualFulfillmentPlanIds(state, selectedOrderIds);
  const selectedFulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds: selectedFulfillmentPlanIds });
  const userHasOrderManagementRole = Global.selectUserhasOrderManagementRole(state);
  const userActiveManualStore = Global.selectSellerActiveManualStores(state);
  const userActiveManualStoreCount = userActiveManualStore.length;
  const salesOrders = Global.selectEntitiesSalesOrdersByIds(state, {
    salesOrderIds: actualSelectedSalesOrderIds,
  });
  const uniqueMarketPlaceIds = uniqueMarketPlaceIdsSelector(salesOrders);
  const disableRestore = uniqueMarketPlaceIds.some((id) => id === MarketplaceIds.AuctaneFulfillment.toString());
  const containsDemoOrder = selectedFulfillmentPlans.some((ffp) => ffp.isDemoOrder);

  return {
    selectedOrderIds,
    actualSelectedSalesOrderIds,
    selectedFulfillmentPlanIds,
    selectedFulfillmentPlans,
    userHasOrderManagementRole,
    userHasActiveManualStore: userActiveManualStoreCount > 0,
    disableRestore,
    containsDemoOrder,
  };
};

const mapDispatchToProps = (dispatch) => {
  const actionTypes = {
    'Remove Hold': {
      messageDescriptor: messages.orderReusableGridButtonRemoveHold,
      actionHandler: (selectedFulfillmentPlanIds) => dispatch(launchRemoveHoldModal(selectedFulfillmentPlanIds, true)),
      theme: 'tertiary',
    },
    'Mark as Paid': {
      messageDescriptor: messages.orderReusableGridButtonMarkAsPaid,
      actionHandler: (selectedSalesOrderId) => dispatch(openMarkAsPaidModal(selectedSalesOrderId)),
      theme: 'tertiary',
    },
    'Mark as Shipped': {
      messageDescriptor: messages.orderReusableGridButtonMarkAsShipped,
      actionHandler: () => dispatch(orderGridOpenMarkShippedModal(BASE_MAS_PAGE)),
      theme: 'tertiary',
    },
    Hold: {
      messageDescriptor: messages.orderReusableGridButtonHold,
      actionHandler: (selectedFulfillmentPlanIds) => dispatch(launchHoldUntilModal(selectedFulfillmentPlanIds)),
      theme: 'tertiary',
    },
    'Restore Order': {
      messageDescriptor: messages.orderReusableGridRestoreOrder,
      actionHandler: (selectedOrderIds) => dispatch(requestRestoreOrder(selectedOrderIds)),
      theme: 'primary',
    },
    Restore: {
      messageDescriptor: messages.orderReusableGridRestore,
      actionHandler: (selectedFulfillmentPlanIds) => dispatch(requestRestoreFulfillments(selectedFulfillmentPlanIds)),
      theme: 'primary',
    },
    'New Order': {
      messageDescriptor: messages.orderReusableGridNewOrder,
      tooltipMessageDescriptor: messages.orderReusableGridNewOrderTooltip,
      actionHandler: () => dispatch(prepareToOpenNewOrderModal()),
      theme: 'tertiary',
    },
    'Restore Shipment': {
      messageDescriptor: messages.orderReusableGridRestoreShipment,
      tooltipMessageDescriptor: messages.orderReusableGridRestoreShipmentTooltip,
      hasTooltip: true,
      theme: 'primary',
      actionHandler: () =>
        ModalService.launchModal({
          component: RestoreModal,
          height: '100%',
          maxHeight: '100%',
          props: {
            onClose: () => {
              ModalService.closeLastOpenModal();
              const unbindFromEvent = PusherService.bindToEvent('bulk-action:restoreWorkflow', (message) => {
                ModalService.launchModal({
                  component: RestoreResultsModal,
                  props: {
                    data: message.data,
                    onClose: () => ModalService.closeLastOpenModal(),
                  },
                });
                unbindFromEvent();
              });
            },
          },
        }),
    },
    'Get Rate': {
      messageDescriptor: messages.orderReusableGridGetRate,
      actionHandler: () => dispatch(orderGridGetRates()),
      theme: 'tertiary',
    },
    'Import Orders': {
      messageDescriptor: messages.orderReusableGridImportOrders,
      theme: 'tertiary',
    },
  };

  return actionTypes;
};

const mergeProps = (
  {
    selectedOrderIds,
    actualSelectedSalesOrderIds,
    selectedFulfillmentPlanIds,
    userHasOrderManagementRole,
    userHasActiveManualStore,
    disableRestore,
    containsDemoOrder,
  },
  propsFromDispatch,
  { id, openCSVImport },
) => {
  const selectedOrders = !!selectedOrderIds.length;
  const { actionHandler } = propsFromDispatch[id];

  const mergedProps = {
    ...propsFromDispatch,
  };

  mergedProps[id].disable = !selectedOrders;

  if (id === 'Mark as Paid') {
    mergedProps[id].disable = !selectedOrders || selectedOrderIds.length > 1;
    return {
      ...mergedProps[id],
      actionHandler: () => actionHandler(actualSelectedSalesOrderIds[0]),
    };
  }

  if (id === 'Restore') {
    const hasSelectedFulfillmentPlans = !!selectedFulfillmentPlanIds.length;
    mergedProps[id].disable = !hasSelectedFulfillmentPlans;
    mergedProps[id].hasTooltip = false;

    if (disableRestore) {
      mergedProps[id].hasTooltip = true;
      mergedProps[id].disable = true;
      mergedProps[id].tooltipMessageDescriptor = messages.orderReusableGridRestoreDisabledTooltip;
    }

    return {
      ...mergedProps[id],
      actionHandler: () => actionHandler(selectedFulfillmentPlanIds),
    };
  }

  if (id === 'New Order') {
    mergedProps[id].disable = !userHasOrderManagementRole || !userHasActiveManualStore;
    mergedProps[id].hasTooltip = !userHasActiveManualStore;
  }
  if (id === 'Import Orders') {
    mergedProps[id].disable = false;
    return {
      ...propsFromDispatch[id],
      actionHandler: () => {
        trackWorkflow(Workflows.ORDER_IMPORT_FLOW, WorkflowSteps.START, {
          source: EventSourceStrings.ORDER_GRID_HEADER_NEW_GRID_BUTTON,
        });
        openCSVImport();
      },
    };
  }

  const keysOfActionHandlersWithParams = ['Remove Hold', 'Restore Order', 'Hold'];

  if (keysOfActionHandlersWithParams.includes(id)) {
    if (id === 'Hold' && containsDemoOrder) {
      mergedProps[id].disable = true;
      mergedProps[id].hasTooltip = true;
      mergedProps[id].tooltipMessageDescriptor = messages.isDemoDisabledMessage;
    }

    return {
      ...mergedProps[id],
      actionHandler: () => actionHandler(selectedOrderIds),
    };
  }

  return {
    ...mergedProps[id],
  };
};

export const OrdersReusableGridButtonContainer = connect(mapStateToProps, mapDispatchToProps, mergeProps)(ReusableGridButton);
